import { formatKpiValue } from '../../utils/strategicKpiFormat';

interface Props {
    unit: string;
    actual: number | null;
    /** Visits, definition version 2: meetings after the as-of date to the month end — drawn hatched, never inside the actual. */
    planned?: number;
    prior: number | null;
    /** The YTD share of the target (visits, GP amounts) — the plan tick the actual is judged against. */
    targetYtd: number | null;
    /** The year target — the plan tick of every other KPI; for a YTD-share KPI a faint dashed line (the tick judges). */
    targetYear: number | null;
    /** The KPI is judged against the YTD share (`judgedOnYtdShare`): visits, GP amounts v2 (plan answer 21). */
    ytdShare: boolean;
    lowerIsBetter?: boolean;
    /** A common scale for a list of bullets (IBCS: identical scaling across comparable marks) — the bar's own values still widen it if larger. */
    scaleMax?: number;
    width?: number | string;
    height?: number;
    className?: string;
}

/** The hatching of the planned meetings: the actual's graphite on the page's white, 45°, 2 px on / 3 px off. */
const PLANNED_FILL = 'repeating-linear-gradient(45deg, var(--sc-ac) 0 2px, var(--surface) 2px 5px)';

/**
 * The IBCS bullet of one KPI in scenario notation (masterplan skpi-redesign E8, mockup «AC · PY · Plan»): the
 * actual is the solid graphite bar (`--sc-ac`), the prior year a thinner light bar above it with its 1 px edge
 * (`--sc-py`, `--sc-py-edge`), the plan a 3 px tick with a 1 px white ring (`--sc-pl`) — for visits the YTD share
 * of the target, for every other KPI the target; planned meetings are hatched behind the actual. The scale is the
 * largest of them plus 6 %, so nothing is clipped and an over-achiever runs past the tick. Every colour is a token
 * of the scoped kit (the element lives under `.skpi`); the marks are neutral — the status dot beside it carries the
 * light (IBCS: colour for variances only).
 */
export function BulletBar({ unit, actual, planned, prior, targetYtd, targetYear, ytdShare, lowerIsBetter, scaleMax, width = 168, height = 22, className = '' }: Props) {
    const a = actual ?? 0, pl = planned ?? 0, py = prior ?? 0, ty = targetYear ?? 0, tYtd = targetYtd ?? 0;
    const max = Math.max(a + pl, py, ty, tYtd, scaleMax ?? 0, 1e-9) * 1.06;
    const pct = (x: number) => `${Math.max(0, Math.min(100, (x / max) * 100)).toFixed(2)}%`;
    const pyH = Math.max(3, Math.round(height * 0.28));
    const acH = Math.max(6, Math.round(height * 0.46));
    const acTop = height - acH - 1;
    const plan = ytdShare ? tYtd : ty;
    const planTitle = ytdShare ? `YTD target ${formatKpiValue(unit, targetYtd)}` : `Target ${formatKpiValue(unit, targetYear)}`;
    return (
        <div className={`relative shrink-0 ${className}`} style={{ width, height }} aria-hidden data-bullet>
            {py > 0 && (
                <div className="absolute left-0" style={{ top: 1, height: pyH, width: pct(py), background: 'var(--sc-py)', boxShadow: 'inset 0 0 0 1px var(--sc-py-edge)', borderRadius: '0 var(--chart-radius) var(--chart-radius) 0' }} title={`Prior year ${formatKpiValue(unit, prior)}`} />
            )}
            {a > 0 && (
                <div className="absolute left-0" style={{ top: acTop, height: acH, width: pct(a), background: 'var(--sc-ac)', borderRadius: '0 var(--chart-radius) var(--chart-radius) 0' }} title={`Actual ${formatKpiValue(unit, actual)}${lowerIsBetter ? ' (lower is better)' : ''}`} />
            )}
            {pl > 0 && (
                <div
                    className="absolute box-border"
                    style={{ left: pct(a), top: acTop, height: acH, width: pct(pl), border: '1px solid var(--sc-ac)', background: PLANNED_FILL }}
                    title={`+ ${planned} planned to the month end (not in the actual)`}
                />
            )}
            {ytdShare && ty > 0 && (
                <div className="absolute" style={{ left: `calc(${pct(ty)} - 1px)`, top: -2, height: height + 4, width: 0, borderLeft: '1px dashed var(--sc-py-edge)' }} title={`Year target ${formatKpiValue(unit, targetYear)}`} />
            )}
            {plan > 0 && (
                <div className="absolute" style={{ left: `calc(${pct(plan)} - 1px)`, top: 0, height, width: 3, background: 'var(--sc-pl)', boxShadow: '0 0 0 1px var(--surface)' }} title={planTitle} />
            )}
        </div>
    );
}

/**
 * The legend of the bullet marks, for the foot of a table or a reading guide. Text is the host's (`tbl-foot`: 13 px,
 * `--text-muted`); the swatches are the marks themselves. `ytdShare` names the KPIs judged against the YTD
 * share ('' = the one KPI of the table, false = none); `planned` shows the planned meetings.
 */
export function BulletLegend({ ytdShare = 'visits', planned = true }: { ytdShare?: string | false; planned?: boolean }) {
    const swatch = 'inline-block shrink-0';
    const targetLabel = ytdShare === false ? 'Target' : ytdShare === '' ? 'Target (YTD share)' : `Target (${ytdShare}: YTD share)`;
    return (
        <span className="inline-flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1.5"><span className={swatch} style={{ width: 14, height: 8, background: 'var(--sc-ac)', borderRadius: 1 }} />Actual</span>
            <span className="inline-flex items-center gap-1.5"><span className={swatch} style={{ width: 14, height: 8, background: 'var(--sc-py)', boxShadow: 'inset 0 0 0 1px var(--sc-py-edge)', borderRadius: 1 }} />Prior year</span>
            <span className="inline-flex items-center gap-1.5"><span className={swatch} style={{ width: 3, height: 12, background: 'var(--sc-pl)' }} />{targetLabel}</span>
            {ytdShare !== false && <span className="inline-flex items-center gap-1.5"><span className={swatch} style={{ width: 0, height: 12, borderLeft: '1px dashed var(--sc-py-edge)' }} />Year target</span>}
            {planned && <span className="inline-flex items-center gap-1.5"><span className={swatch} style={{ width: 14, height: 8, border: '1px solid var(--sc-ac)', background: PLANNED_FILL }} />Planned meetings</span>}
        </span>
    );
}
